import { useMemo } from "react"
import { useQuery } from "urql"

import { parseUserList, useAccountManagers } from "./useAccountManagers"

import type { Query, User } from "@/graphql/codegen/graphql"
import { UsersListDocument } from "@/graphql/codegen/graphql"
import { useAccountManagerStore } from "@/stores/accountManagerStore"

interface UseManagersParams {
  query?: string
}

interface UseManagersReturn {
  visibleManagers: User[]
  fetching: boolean
}

// The same person can be both an account manager and a product manager, so list each user once.
const mergeManagers = (accountManagers: User[], productManagers: User[]): User[] =>
  [...new Map([...accountManagers, ...productManagers].map((manager) => [manager.userId, manager])).values()].sort(
    (a, b) => a.name.localeCompare(b.name)
  )

// The account managers and the product managers, for filtering by any manager of a company.
export const useManagers = ({ query = "" }: UseManagersParams = {}): UseManagersReturn => {
  const limit = useAccountManagerStore((state) => state.limit)
  const { visibleManagers: accountManagers, fetching: accountManagersFetching } = useAccountManagers({ query })

  const searchFilter = query && query?.length > 1 && { query }

  const [{ data, fetching: productManagersFetching }] = useQuery<Pick<Query, "user">>({
    query: UsersListDocument,
    variables: {
      input: { filter: { ...searchFilter, isAs400ProductManager: true }, limit },
    },
  })

  const productManagers = useMemo(() => parseUserList(data) ?? [], [data])

  const visibleManagers = useMemo(
    () => mergeManagers(accountManagers, productManagers),
    [accountManagers, productManagers]
  )

  return { visibleManagers, fetching: accountManagersFetching || productManagersFetching }
}
